"use client";

import { useEffect, useState } from "react";

const words = ["STARTUP", "AI AGENT", "ENTERPRISE", "PRODUCT", "IDEA"];

export default function HeroText() {
    const [wordIndex, setWordIndex] = useState(4);
    const [currentText, setCurrentText] = useState("IDEA");
    const [status, setStatus] = useState<"typing" | "waiting" | "blurring">("waiting");
    // Track hydration so the initial word renders fully visible (no per-char animation)
    // to prevent the LCP element (h1) from being invisible on first paint.
    const [hasHydrated, setHasHydrated] = useState(false);

    useEffect(() => {
        setHasHydrated(true);
    }, []);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        const activeWord = words[wordIndex];

        if (status === "typing") {
            const handleType = () => {
                const nextText = activeWord.substring(0, currentText.length + 1);
                setCurrentText(nextText);
                if (nextText === activeWord) {
                    setStatus("waiting");
                }
            };
            timer = setTimeout(handleType, 80);
        } else if (status === "waiting") {
            // Keep the word fully visible for 2 seconds
            timer = setTimeout(() => {
                setStatus("blurring");
            }, 2000);
        } else if (status === "blurring") {
            // Melt away (fade + blur) for 0.3s, then instantly clear and type next
            timer = setTimeout(() => {
                setCurrentText("");
                setStatus("typing");
                setWordIndex((prev) => (prev + 1) % words.length);
            }, 300);
        }

        return () => clearTimeout(timer);
    }, [currentText, status, wordIndex]);

    return (
        <span className="relative inline-grid grid-cols-1 items-center min-h-[1.1em] align-middle select-none">
            {/* Invisible spacer to reserve width of the current text to prevent CLS */}
            <span className="invisible whitespace-nowrap pl-1 pr-3 py-0.5 font-medium italic pointer-events-none col-start-1 row-start-1">
                {currentText}
            </span>
            <span
                className={`col-start-1 row-start-1 inline-flex items-center justify-start whitespace-nowrap transition-all duration-300 ease-out pl-1 pr-3 py-0.5 font-medium italic text-zinc-950 ${
                    status === "blurring"
                        ? "opacity-0 blur-md scale-95"
                        : "opacity-100 blur-none scale-100"
                }`}
            >
                {/* Before hydration, render the word as a single visible span (no animation)
                    so the LCP text is painted immediately. After hydration, animate each char. */}
                {hasHydrated ? (
                    currentText.split("").map((char, index) => (
                        <span
                            key={`${index}-${char}`}
                            className="animate-char-in"
                        >
                            {char === " " ? "\u00A0" : char}
                        </span>
                    ))
                ) : (
                    <span>{currentText}</span>
                )}
                {/* Slanted cursor (skewed to match standard italic text slope) */}
                <span className="ml-0.5 inline-block w-[2px] h-[1.05em] bg-zinc-900 animate-cursor-blink self-center shrink-0 origin-center transform skew-x-[-12deg]" />
            </span>
        </span>
    );
}
